import * as _ from 'styles/helpers'

/**
 * The Demographics charts' data, as a pure function of the figures (MYD-499).
 *
 * Extracted from DemographicsCharts.tsx so the Demographics tab and a
 * presentation slide build exactly the same charts from the same numbers. The
 * tab feeds it from the store; a slide feeds it from its resolved payload.
 *
 * Three areas are compared: the postal code, its city, and its CBSA. Any of
 * them may be missing, in which case its values are undefined and Chart.js
 * leaves a gap, as the tab always has while a request is in flight.
 */

export interface AreaFigures {
	population?: number | null
	householdsPerZipCode?: number | null
	incomePerHousehold?: number | null
	averageHouseValue?: number | null
	femalePopulation?: number | null
	malePopulation?: number | null
	asianPopulation?: number | null
	blackPopulation?: number | null
	hispanicPopulation?: number | null
	indianPopulation?: number | null
	otherPopulation?: number | null
	whitePopulation?: number | null
	medianAge?: number | null
}

export type AreaKey = 'postalCode' | 'city' | 'cbsa'

export type DemographicAreas = Partial<Record<AreaKey, AreaFigures | null | undefined>>

/** How each area is named on the charts: the postal code itself, the city's name, 'CBSA'. */
export type AreaLabels = Record<AreaKey, string>

export const AREA_KEYS: AreaKey[] = ['postalCode', 'city', 'cbsa']

export const ETHNICITY_LABELS = ['Asian', 'Black', 'Hispanic', 'Indian', 'Other', 'White']

const ETHNICITY_FIELDS: Array<keyof AreaFigures> = [
	'asianPopulation',
	'blackPopulation',
	'hispanicPopulation',
	'indianPopulation',
	'otherPopulation',
	'whitePopulation',
]

const ETHNICITY_COLORS = [_.COLORS.orange_200, _.COLORS.orange_300, _.COLORS.orange_400, _.COLORS.blue_300, _.COLORS.blue_400, '#0080fe']

const GENDER_COLORS = ['#0080fe', _.COLORS.blue_200]

const across = (areas: DemographicAreas, field: keyof AreaFigures) => AREA_KEYS.map(key => areas[key]?.[field] ?? undefined)

const bars = (labels: AreaLabels, datasets: Array<{ label: string; data: Array<number | undefined>; backgroundColor: string }>) => ({
	labels: AREA_KEYS.map(key => labels[key]),
	datasets,
})

const gender = (figures: AreaFigures | null | undefined) => ({
	labels: ['Male', 'Female'],
	datasets: [{ data: [figures?.malePopulation ?? undefined, figures?.femalePopulation ?? undefined], backgroundColor: GENDER_COLORS, borderWidth: 1 }],
})

const ethnicity = (figures: AreaFigures | null | undefined) => ({
	labels: ETHNICITY_LABELS,
	datasets: [{ data: ETHNICITY_FIELDS.map(field => figures?.[field] ?? undefined), backgroundColor: ETHNICITY_COLORS, borderWidth: 1 }],
})

export const buildDemographicCharts = (areas: DemographicAreas, labels: AreaLabels) => ({
	population: bars(labels, [{ label: 'Dataset 1', data: across(areas, 'population'), backgroundColor: _.COLORS.tradewind_blue }]),
	households: bars(labels, [{ label: 'Dataset 1', data: across(areas, 'householdsPerZipCode'), backgroundColor: _.COLORS.tradewind_blue }]),
	incomeVsHomePrice: bars(labels, [
		{ label: 'Income', data: across(areas, 'incomePerHousehold'), backgroundColor: _.COLORS.tradewind_blue },
		{ label: 'Home Price', data: across(areas, 'averageHouseValue'), backgroundColor: _.COLORS.orange_400 },
	]),
	gender: { postalCode: gender(areas.postalCode), city: gender(areas.city), cbsa: gender(areas.cbsa) },
	ethnicity: { postalCode: ethnicity(areas.postalCode), city: ethnicity(areas.city), cbsa: ethnicity(areas.cbsa) },
})

export type DemographicCharts = ReturnType<typeof buildDemographicCharts>
